import { PAGE_PATH } from '@/lib/page-paths'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { memo } from 'react'
import { FaBookmark } from 'react-icons/fa'
import { FiBookmark, FiMoreVertical } from 'react-icons/fi'

type ThreadItemProps = {
  id: string
  title: string
  updatedAt: string
  isBookmarked: boolean
  isActive: boolean
  isEditing: boolean
  editingName: string
  onEditingNameChange: (name: string) => void
  onToggleFavorite: () => void
  onMenuClick: (buttonRef: HTMLButtonElement) => void
  onRenameSubmit: () => void
  onCancelRename: () => void
  menuButtonRef: (el: HTMLButtonElement | null) => void
}

export const ThreadItem = memo(function ThreadItem({
  id,
  title,
  updatedAt,
  isBookmarked,
  isActive,
  isEditing,
  editingName,
  onEditingNameChange,
  onToggleFavorite,
  onMenuClick,
  onRenameSubmit,
  onCancelRename,
  menuButtonRef,
}: ThreadItemProps) {
  if (isEditing) {
    return (
      <div className="bg-ui-hover rounded-lg px-3 py-2">
        <input
          type="text"
          value={editingName}
          onChange={(e) => onEditingNameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onRenameSubmit()
            } else if (e.key === 'Escape') {
              onCancelRename()
            }
          }}
          onBlur={onRenameSubmit}
          autoFocus
          className="text-ui-text w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm outline-none focus:border-gray-500"
        />
      </div>
    )
  }

  return (
    <>
      <motion.div whileHover={{ x: isActive ? 0 : 4 }} transition={{ duration: 0.2 }}>
        <Link
          href={`${PAGE_PATH.COPILOT}/${id}`}
          onClick={(e) => {
            if (isActive) {
              e.preventDefault()
            }
          }}
          className={cn(
            'block rounded-lg px-3 py-2 pr-8 text-sm transition-all',
            isActive && 'bg-ui-hover font-medium'
          )}
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-ui-text line-clamp-2 h-10">{title}</div>
              <div className="text-ui-secondary mt-1 text-xs">
                {new Date(updatedAt).toLocaleString(undefined, {
                  month: 'numeric',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}
              </div>
            </div>
          </div>
        </Link>
      </motion.div>

      {/* Bookmark button */}
      <motion.button
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onToggleFavorite()
        }}
        className={cn(
          'hover:bg-ui-hover absolute right-2 top-2 rounded p-1 transition-all',
          isBookmarked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        )}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
      >
        <motion.div
          animate={
            isBookmarked
              ? {
                  scale: [1, 1.2, 1],
                  rotate: [0, -10, 10, 0],
                }
              : { scale: 1 }
          }
          transition={{ duration: 0.3 }}
        >
          {isBookmarked ? (
            <FaBookmark className="h-3.5 w-3.5 text-blue-600 transition-colors" />
          ) : (
            <FiBookmark className="text-ui-secondary h-3.5 w-3.5 transition-colors hover:text-blue-600" />
          )}
        </motion.div>
      </motion.button>

      {/* Three-dot menu */}
      <div className="absolute bottom-2 right-2">
        <button
          ref={menuButtonRef}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onMenuClick(e.currentTarget)
          }}
          className="hover:bg-ui-hover rounded p-1 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Thread options"
        >
          <FiMoreVertical className="text-ui-secondary h-4 w-4" />
        </button>
      </div>
    </>
  )
})
